import { z } from 'zod'

/**
 * Состояния бота и наблюдаемые детали. Источник истины — воркер (docs/02 §5),
 * API кэширует последнее известное значение в `bot.lastState` / `bot.lastDetail`.
 */

/**
 * Ровно 11 состояний из docs/02 §5 (и enum `BotState` в Prisma-схеме, docs/05 §2).
 * Порядок сохранён как в документе — он же порядок «жизненного цикла».
 */
export const BOT_STATES = [
  'STOPPED',
  'QUEUED',
  'CONNECTING',
  'LOGIN',
  'CONFIG',
  'SPAWNING',
  'PLAYING',
  'DEGRADED',
  'RECONNECT_WAIT',
  'AUTH_FAILED',
  'UNKNOWN'
] as const

export const BotState = z.enum(BOT_STATES)
export type BotState = z.infer<typeof BotState>

/** Состояния, в которых бот считается «живым» (виден на сервере или идёт к нему). */
export const ACTIVE_BOT_STATES = [
  'QUEUED',
  'CONNECTING',
  'LOGIN',
  'CONFIG',
  'SPAWNING',
  'PLAYING',
  'DEGRADED'
] as const satisfies readonly BotState[]

/** Декларативная цель, хранится в БД (`bot.desiredState`). */
export const DESIRED_STATES = ['RUNNING', 'STOPPED'] as const
export const DesiredState = z.enum(DESIRED_STATES)
export type DesiredState = z.infer<typeof DesiredState>

/** Уровни логов бота (Prisma enum `LogLevel`, docs/05 §2). */
export const LOG_LEVELS = ['DEBUG', 'INFO', 'CHAT', 'WARN', 'ERROR'] as const
export const LogLevel = z.enum(LOG_LEVELS)
export type LogLevel = z.infer<typeof LogLevel>

/**
 * Известные значения `bot_event.kind` (docs/05 §2 + docs/07: `compat`, `death`, `error`).
 * На проводе `kind` — свободная строка: рантайм может ввести новый вид события
 * раньше, чем обновится API, и это не должно ломать валидацию (docs/06 §8
 * описывает поле как `string`). `KNOWN_EVENT_KINDS` нужен UI для фильтров.
 */
export const KNOWN_EVENT_KINDS = [
  'state',
  'chat',
  'kick',
  'hook',
  'parseError',
  'command',
  'auth',
  'compat',
  'death',
  'error'
] as const
export type KnownEventKind = (typeof KNOWN_EVENT_KINDS)[number]

export const EventKind = z.string().min(1).max(64)
export type EventKind = z.infer<typeof EventKind>

/** Тип авторизации MC-аккаунта (Prisma enum `McAuthType`). */
export const MC_AUTH_TYPES = ['MICROSOFT', 'OFFLINE'] as const
export const McAuthType = z.enum(MC_AUTH_TYPES)
export type McAuthType = z.infer<typeof McAuthType>

/** Статус MC-аккаунта (Prisma enum `McAccountStatus`). */
export const MC_ACCOUNT_STATUSES = [
  'NEW',
  'AUTH_PENDING',
  'READY',
  'AUTH_FAILED',
  'EXPIRED',
  'DISABLED'
] as const
export const McAccountStatus = z.enum(MC_ACCOUNT_STATUSES)
export type McAccountStatus = z.infer<typeof McAccountStatus>

/** Позиция в мире. Отдельно, потому что используется и в BotDetail, и в кадрах камеры. */
export const XYZ = z.object({ x: z.number(), y: z.number(), z: z.number() })
export type XYZ = z.infer<typeof XYZ>

/**
 * `bot.lastDetail` — наблюдаемые данные для UI (docs/05 §4).
 * Все поля nullable: воркер может ещё не знать значения (например, до spawn).
 */
export const BotDetail = z.object({
  username: z.string().nullable(),
  pos: XYZ.nullable(),
  dimension: z.string().nullable(),
  yaw: z.number().nullable(),
  pitch: z.number().nullable(),
  health: z.number().nullable(),
  food: z.number().nullable(),
  /**
   * Игровой режим с точки зрения сервера: `survival` | `creative` |
   * `adventure` | `spectator`.
   *
   * Поле появилось не для полноты картины, а потому что без него панель врала
   * ощущением: у бота в креативе здоровье всегда 20, его не бьют, не толкает
   * лава — и это выглядит как «сломанная физика», хотя сервер просто выдаёт
   * креатив всем входящим (`gamemode=creative` в server.properties).
   */
  gameMode: z.string().nullable(),
  ping: z.number().nullable(),
  playersOnline: z.number().nullable(),
  loadedChunks: z.number().nullable(),
  version: z.string().nullable(),
  capabilities: z.record(z.boolean()).nullable(),
  parseErrors: z.object({ total: z.number(), unknown: z.number() }).nullable()
})
export type BotDetail = z.infer<typeof BotDetail>

/** Пустая деталь: всё неизвестно. Удобно как стартовое значение в воркере и в тестах. */
export function emptyBotDetail(): BotDetail {
  return {
    username: null,
    pos: null,
    dimension: null,
    yaw: null,
    pitch: null,
    health: null,
    food: null,
    gameMode: null,
    ping: null,
    playersOnline: null,
    loadedChunks: null,
    version: null,
    capabilities: null,
    parseErrors: null
  }
}
